import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '@/lib/backendUrl';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { SimpleModal } from '@/components/SimpleModal';
import { useToast } from '@/hooks/use-toast';
import { formatCurrency } from '@/lib/utils';
import { 
  FileText, 
  Printer, 
  Calendar, 
  User, 
  DollarSign, 
  TrendingUp, 
  TrendingDown,
  Package,
  ShoppingCart,
  RefreshCw,
  Download,
  Clock,
  Wallet,
  ArrowUpCircle,
  ArrowDownCircle
} from 'lucide-react';

const API_URL = BACKEND_URL;

export const RelatoriosCaixaPage = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [caixas, setCaixas] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [resumo, setResumo] = useState(null);
  const [resumoProdutos, setResumoProdutos] = useState(null);
  
  // Filtros
  const [dataInicio, setDataInicio] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [dataFim, setDataFim] = useState(() => new Date().toISOString().split('T')[0]);
  const [userFilter, setUserFilter] = useState('todos');
  
  // Modal de impressão
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [printContent, setPrintContent] = useState('');
  const [selectedCaixa, setSelectedCaixa] = useState(null);
  const [expandedCaixa, setExpandedCaixa] = useState(null);

  // Carregar usuários
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/api/tenant/users`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        setUsers(data.users || []);
      } catch (error) {
        console.error('Erro ao carregar usuários');
      }
    };
    fetchUsers();
  }, []);

  // Carregar relatório de caixas
  const loadRelatorioCaixas = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      let url = `${API_URL}/api/relatorios/caixas?data_inicio=${dataInicio}&data_fim=${dataFim}`;
      if (userFilter !== 'todos') {
        url += `&user_id=${userFilter}`;
      }
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setCaixas(data.caixas || []);
      setResumo(data.resumo || null);
    } catch (error) {
      toast({ title: 'Erro', description: 'Erro ao carregar relatório', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  // Carrega o relatório automaticamente ao abrir a página
  useEffect(() => {
    loadRelatorioCaixas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Carregar relatório de produtos
  const loadRelatorioProdutos = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const url = `${API_URL}/api/relatorios/vendas-por-produto?data_inicio=${dataInicio}&data_fim=${dataFim}`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setProdutos(data.produtos || []);
      setResumoProdutos(data.resumo || null);
    } catch (error) {
      toast({ title: 'Erro', description: 'Erro ao carregar relatório', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  // Imprimir relatório de caixa
  const imprimirCaixa = async (caixaId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/relatorios/impressao/caixa/${caixaId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setPrintContent(data.relatorio);
      setSelectedCaixa(data.caixa);
      setShowPrintModal(true);
    } catch (error) {
      toast({ title: 'Erro', description: 'Erro ao gerar relatório', variant: 'destructive' });
    }
  };

  // Imprimir relatório de vendas por período
  const imprimirVendasPeriodo = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/relatorios/impressao/vendas-periodo?data_inicio=${dataInicio}&data_fim=${dataFim}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setPrintContent(data.relatorio);
      setSelectedCaixa(null);
      setShowPrintModal(true);
    } catch (error) {
      toast({ title: 'Erro', description: 'Erro ao gerar relatório', variant: 'destructive' });
    }
  };

  // Função para imprimir
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Relatório</title>
          <style>
            body { font-family: 'Courier New', monospace; font-size: 12px; white-space: pre; }
            @media print { body { margin: 0; padding: 10px; } }
          </style>
        </head>
        <body>${printContent}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  // Copiar para área de transferência
  const handleCopy = () => {
    navigator.clipboard.writeText(printContent);
    toast({ title: 'Copiado!', description: 'Relatório copiado para área de transferência' });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('pt-BR', { 
      day: '2-digit', 
      month: '2-digit', 
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="p-8 max-w-7xl mx-auto" data-testid="relatorios-caixa-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
            <FileText className="w-8 h-8 text-emerald-600" />
            Relatórios de Caixa
          </h1>
          <p className="text-slate-500 mt-1">Análise de caixas, vendas e produtos</p>
        </div>
      </div>

      {/* Filtros */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div>
              <Label>Data Início</Label>
              <Input
                type="date"
                value={dataInicio}
                onChange={(e) => setDataInicio(e.target.value)}
                className="w-40"
                data-testid="data-inicio"
              />
            </div>
            <div>
              <Label>Data Fim</Label>
              <Input
                type="date"
                value={dataFim}
                onChange={(e) => setDataFim(e.target.value)}
                className="w-40"
                data-testid="data-fim"
              />
            </div>
            <div>
              <Label>Operador</Label>
              <Select value={userFilter} onValueChange={setUserFilter}>
                <SelectTrigger className="w-48" data-testid="user-filter">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos os operadores</SelectItem>
                  {users.map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={loadRelatorioCaixas} disabled={loading} data-testid="btn-buscar-caixas">
              {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Buscar Caixas
            </Button>
            <Button onClick={loadRelatorioProdutos} variant="outline" disabled={loading} data-testid="btn-buscar-produtos">
              <Package className="w-4 h-4 mr-2" />
              Buscar Produtos
            </Button>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="caixas" className="space-y-6">
        <TabsList>
          <TabsTrigger value="caixas">
            <Wallet className="w-4 h-4 mr-2" />
            Caixas
          </TabsTrigger>
          <TabsTrigger value="produtos">
            <Package className="w-4 h-4 mr-2" />
            Produtos Vendidos
          </TabsTrigger>
        </TabsList>

        {/* Aba Caixas */}
        <TabsContent value="caixas">
          {/* Resumo */}
          {resumo && (
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
              <Card>
                <CardContent className="pt-6 text-center">
                  <p className="text-sm text-slate-500">Caixas</p>
                  <p className="text-2xl font-bold">{resumo.quantidade}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6 text-center">
                  <p className="text-sm text-slate-500">Total Vendas</p>
                  <p className="text-2xl font-bold text-emerald-600">{formatCurrency(resumo.total_vendas)}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6 text-center">
                  <p className="text-sm text-slate-500">Sangrias</p>
                  <p className="text-2xl font-bold text-red-600">{formatCurrency(resumo.total_sangrias)}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6 text-center">
                  <p className="text-sm text-slate-500">Suprimentos</p>
                  <p className="text-2xl font-bold text-blue-600">{formatCurrency(resumo.total_suprimentos)}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6 text-center">
                  <p className="text-sm text-slate-500">Diferença</p>
                  <p className={`text-2xl font-bold ${resumo.total_diferenca >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {formatCurrency(resumo.total_diferenca)}
                  </p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Lista de Caixas */}
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Histórico de Caixas</CardTitle>
                  <CardDescription>Lista de caixas no período selecionado</CardDescription>
                </div>
                <Button onClick={imprimirVendasPeriodo} variant="outline" disabled={caixas.length === 0}>
                  <Printer className="w-4 h-4 mr-2" />
                  Imprimir Período
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {caixas.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <Wallet className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Nenhum caixa encontrado no período</p>
                  <p className="text-sm">Clique em "Buscar Caixas" para carregar</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="caixas-table">
                    <thead>
                      <tr className="border-b text-sm">
                        <th className="text-left py-2 px-3">Operador</th>
                        <th className="text-left py-2 px-3">Abertura</th>
                        <th className="text-left py-2 px-3">Fechamento</th>
                        <th className="text-right py-2 px-3">Vendas</th>
                        <th className="text-right py-2 px-3">Total</th>
                        <th className="text-right py-2 px-3">Diferença</th>
                        <th className="text-center py-2 px-3">Status</th>
                        <th className="text-right py-2 px-3">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {caixas.map(caixa => (
                        <React.Fragment key={caixa.id}>
                        <tr className="border-b hover:bg-slate-50">
                          <td className="py-2 px-3 font-medium">{caixa.user_name}</td>
                          <td className="py-2 px-3 text-sm">{formatDate(caixa.abertura_at)}</td>
                          <td className="py-2 px-3 text-sm">{formatDate(caixa.fechamento_at)}</td>
                          <td className="py-2 px-3 text-right">{caixa.vendas_count || 0}</td>
                          <td className="py-2 px-3 text-right font-medium">{formatCurrency(caixa.total_vendas || 0)}</td>
                          <td className={`py-2 px-3 text-right font-medium ${
                            (caixa.diferenca || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'
                          }`}>
                            {caixa.status === 'fechado' ? formatCurrency(caixa.diferenca || 0) : '-'}
                          </td>
                          <td className="py-2 px-3 text-center">
                            <Badge variant={caixa.status === 'aberto' ? 'default' : 'secondary'}>
                              {caixa.status === 'aberto' ? 'Aberto' : 'Fechado'}
                            </Badge>
                          </td>
                          <td className="py-2 px-3 text-right whitespace-nowrap">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setExpandedCaixa(expandedCaixa === caixa.id ? null : caixa.id)}
                              data-testid={`btn-detalhes-${caixa.id}`}
                              title="Ver formas de pagamento"
                            >
                              <Wallet className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => imprimirCaixa(caixa.id)}>
                              <Printer className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                        {expandedCaixa === caixa.id && (
                          <tr className="bg-slate-50 border-b" data-testid={`detalhes-${caixa.id}`}>
                            <td colSpan={8} className="p-4">
                              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                                {(caixa.formas_fechamento && caixa.formas_fechamento.length
                                  ? caixa.formas_fechamento.map((f) => ({
                                      label: f.label || f.code,
                                      esperado: f.esperado || 0,
                                      informado: f.informado,
                                    }))
                                  : [
                                      ['Dinheiro', 'valor_dinheiro'],
                                      ['PIX', 'valor_pix'],
                                      ['Crédito', 'valor_credito'],
                                      ['Débito', 'valor_debito'],
                                      ['Outros', 'valor_outros'],
                                    ].map(([label, campo]) => ({
                                      label,
                                      esperado: caixa[`${campo}_esperado`] || 0,
                                      informado: caixa[`${campo}_informado`],
                                    }))
                                ).map((f) => (
                                  <div key={f.label} className="bg-white rounded-lg border p-3">
                                    <p className="text-xs text-slate-500">{f.label}</p>
                                    <p className="text-sm font-semibold">{formatCurrency(f.esperado)}</p>
                                    {caixa.status === 'fechado' && (
                                      <p className={`text-xs ${
                                        Math.abs((f.informado || 0) - f.esperado) < 0.01 ? 'text-slate-400' : 'text-red-500'
                                      }`}>
                                        apurado: {formatCurrency(f.informado || 0)}
                                      </p>
                                    )}
                                  </div>
                                ))}
                              </div>
                              <div className="flex flex-wrap gap-6 mt-3 text-xs text-slate-500">
                                <span>Abertura: <strong>{formatCurrency(caixa.valor_inicial || 0)}</strong></span>
                                <span>Sangrias: <strong className="text-red-600">{formatCurrency(caixa.total_sangrias || 0)}</strong></span>
                                <span>Suprimentos: <strong className="text-blue-600">{formatCurrency(caixa.total_suprimentos || 0)}</strong></span>
                                <span>Fiado / A Receber: <strong>{formatCurrency(caixa.valor_a_receber_esperado || 0)}</strong></span>
                              </div>
                            </td>
                          </tr>
                        )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Aba Produtos */}
        <TabsContent value="produtos">
          {/* Resumo */}
          {resumoProdutos && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <Card>
                <CardContent className="pt-6 text-center">
                  <p className="text-sm text-slate-500">Produtos Diferentes</p>
                  <p className="text-2xl font-bold">{resumoProdutos.total_produtos}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6 text-center">
                  <p className="text-sm text-slate-500">Total de Itens</p>
                  <p className="text-2xl font-bold text-blue-600">{resumoProdutos.total_itens}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6 text-center">
                  <p className="text-sm text-slate-500">Valor Total</p>
                  <p className="text-2xl font-bold text-emerald-600">{formatCurrency(resumoProdutos.total_valor)}</p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Lista de Produtos */}
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Produtos Vendidos</CardTitle>
                  <CardDescription>Ranking de produtos por quantidade vendida</CardDescription>
                </div>
                <Button onClick={imprimirVendasPeriodo} variant="outline" disabled={produtos.length === 0}>
                  <Printer className="w-4 h-4 mr-2" />
                  Imprimir
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {produtos.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Nenhum produto vendido no período</p>
                  <p className="text-sm">Clique em "Buscar Produtos" para carregar</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="produtos-table">
                    <thead>
                      <tr className="border-b text-sm">
                        <th className="text-left py-2 px-3">#</th>
                        <th className="text-left py-2 px-3">Produto</th>
                        <th className="text-right py-2 px-3">Quantidade</th>
                        <th className="text-right py-2 px-3">Vendas</th>
                        <th className="text-right py-2 px-3">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {produtos.map((p, idx) => (
                        <tr key={p.product_id} className="border-b hover:bg-slate-50">
                          <td className="py-2 px-3 text-slate-400">{idx + 1}</td>
                          <td className="py-2 px-3 font-medium">{p.product_name}</td>
                          <td className="py-2 px-3 text-right">{p.quantidade_total}</td>
                          <td className="py-2 px-3 text-right text-slate-500">{p.vendas_count}</td>
                          <td className="py-2 px-3 text-right font-medium text-emerald-600">{formatCurrency(p.valor_total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modal de Impressão */}
      <SimpleModal
        open={showPrintModal}
        onClose={() => setShowPrintModal(false)}
        title="Relatório para Impressão"
        className="max-w-4xl"
      >
        <div className="space-y-4">
          <div className="flex gap-2 mb-4">
            <Button onClick={handlePrint} className="bg-emerald-600 hover:bg-emerald-700">
              <Printer className="w-4 h-4 mr-2" />
              Imprimir
            </Button>
            <Button onClick={handleCopy} variant="outline">
              <Download className="w-4 h-4 mr-2" />
              Copiar Texto
            </Button>
          </div>
          
          <div className="bg-slate-900 text-green-400 p-4 rounded-lg overflow-x-auto">
            <pre className="font-mono text-xs whitespace-pre" style={{ fontFamily: 'Courier New, monospace' }}>
              {printContent}
            </pre>
          </div>
        </div>
      </SimpleModal>
    </div>
  );
};
